import { useId } from 'react';
import { Check, Info } from '@phosphor-icons/react';
import type { CreatorNote, ImportResult } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { WizardFooter } from './ImportWizardFooterSlot';
import { rejectedRowsByReason, type ImportDoneTally, type RejectedReasonGroup } from './importWizardHelpers';

export interface ImportDoneStepProps {
  result: ImportResult;
  tally: ImportDoneTally;
  /** Creators the commit kept at their current values because the campaign
   *  changed mid-review (a partial roster drift). */
  driftKeptHandles: string[];
  /** Which creators kept their current values couldn't be read back, so the
   *  absence of a kept-values note below isn't confirmation that none did. */
  driftKeptUnconfirmed?: boolean;
  campaignName: string;
  onDone: () => void;
}

const CAMPAIGN_NAME_FALLBACK = 'this campaign';
const BADGE_ICON_SIZE = 24;

function headline(count: number, campaignName: string): string {
  const campaign = campaignName || CAMPAIGN_NAME_FALLBACK;
  if (count === 1) return `1 creator is in ${campaign}`;
  return `${count} creators are in ${campaign}`;
}

function breakdown({ added, updated, kept, skipped }: ImportDoneTally): string {
  return `${added} added and ${updated} updated, ${kept} left as is, ${skipped} skipped. They're on the Creators tab, ready for review.`;
}

function driftKeptNote(handles: string[]): string {
  const subject = handles.length === 1 ? '1 creator' : `${handles.length} creators`;
  const list = handles.map((handle) => `@${handle}`).join(', ');
  return `${subject} kept their current values — the campaign changed while you were reviewing: ${list}`;
}

const DRIFT_KEPT_UNCONFIRMED_NOTE =
  "Couldn't confirm which creators kept their current values. If the campaign changed while you were reviewing, check the Creators tab.";

function DriftKeptNote({ handles, unconfirmed }: { handles: string[]; unconfirmed: boolean }) {
  if (unconfirmed) return <p className="max-w-md text-body-sm-regular text-muted-foreground">{DRIFT_KEPT_UNCONFIRMED_NOTE}</p>;
  if (handles.length === 0) return null;
  return <p className="max-w-md text-body-sm-regular text-muted-foreground">{driftKeptNote(handles)}</p>;
}

function rejectedHeading(count: number): string {
  if (count === 1) return "1 row wasn't imported";
  return `${count} rows weren't imported`;
}

function rejectedRowLabel(note: CreatorNote): string {
  if (!note.username) return `Row ${note.row}`;
  return `Row ${note.row} · @${note.username}`;
}

function countNotes(groups: RejectedReasonGroup[]): number {
  return groups.reduce((total, group) => total + group.notes.length, 0);
}

function RejectedReason({ group }: { group: RejectedReasonGroup }) {
  const headingId = useId();
  return (
    <div role="group" aria-labelledby={headingId} className="flex flex-col gap-1">
      <p id={headingId} className="text-body-sm-medium text-foreground">
        {group.reason}
      </p>
      <ul className="flex flex-col gap-0.5 text-body-sm-regular text-muted-foreground">
        {group.notes.map((note) => (
          <li key={note.row}>{rejectedRowLabel(note)}</li>
        ))}
      </ul>
    </div>
  );
}

function RejectedRows({ notes }: { notes: CreatorNote[] }) {
  const groups = rejectedRowsByReason(notes);
  if (groups.length === 0) return null;
  return (
    <section className="flex w-full max-w-md flex-col gap-3 rounded-xl border border-border p-4 text-left">
      <h4 className="text-body-sm-semibold text-foreground">{rejectedHeading(countNotes(groups))}</h4>
      {groups.map((group) => (
        <RejectedReason key={group.reason} group={group} />
      ))}
    </section>
  );
}

const RESULT_UNAVAILABLE_TITLE = 'The import has ended';
const RESULT_UNAVAILABLE_BODY =
  "Its summary couldn't be loaded, so we can't confirm what was added. Check the campaign's creators before importing again.";

/** The done screen for an import that ended but whose result can't be read
 *  back: it says the outcome is unknown, instead of the wizard waiting on it
 *  forever or claiming success. */
export function ImportDoneWithoutResult({ onDone }: { onDone: () => void }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <span className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Info size={BADGE_ICON_SIZE} weight="bold" aria-hidden="true" />
      </span>
      <h3 className="text-heading-h6 text-foreground">{RESULT_UNAVAILABLE_TITLE}</h3>
      <p className="max-w-md text-body-sm-regular text-muted-foreground">{RESULT_UNAVAILABLE_BODY}</p>
      <WizardFooter>
        <div className="flex w-full justify-end">
          <Button type="button" size="lg" onClick={onDone}>
            Done
          </Button>
        </div>
      </WizardFooter>
    </div>
  );
}

export function ImportDoneStep({ result, tally, driftKeptHandles, driftKeptUnconfirmed = false, campaignName, onDone }: ImportDoneStepProps) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <span className="flex size-14 items-center justify-center rounded-full bg-background-success-light-hover text-success">
        <Check size={BADGE_ICON_SIZE} weight="bold" aria-hidden="true" />
      </span>
      <h3 className="text-heading-h6 text-foreground">{headline(tally.added + tally.updated, campaignName)}</h3>
      <p className="max-w-md text-body-sm-regular text-muted-foreground">{breakdown(tally)}</p>
      <DriftKeptNote handles={driftKeptHandles} unconfirmed={driftKeptUnconfirmed} />
      <RejectedRows notes={result.creator_notes} />
      <WizardFooter>
        <div className="flex w-full justify-end">
          <Button type="button" size="lg" onClick={onDone}>
            Done
          </Button>
        </div>
      </WizardFooter>
    </div>
  );
}
